import type { PartDefinition, MaterialInfo } from "@rogue/rackbuilder"
import { getRackModel, ATTACHMENTS } from "@/js/pages/product/rackbuilder/modelPaths"
import type { RackOptions } from "@/js/pages/product/rackbuilder/RackTypes"
export { createFMRollerPart as createRoller } from "../ftSharedParts"

// LRLP Parts — All part definitions for Lat & Low Row Pulldown (RM and RML variants)

// MARK: Context
export interface LRLPContext {
  config: RackOptions
  materials: MaterialInfo
  fxtMaterials: MaterialInfo
  
  // Extracted config for convenience
  line: RackOptions['line']
  height: RackOptions['height']
  depth: RackOptions['depth']
  isRM: boolean
  isRML: boolean
  isHalfRack: boolean
  isSlimRack: boolean
  areStringersSelected: boolean
  weightType: '250' | '300'
}

// MARK: Top Beam Assembly
export function createTopBeam(context: LRLPContext): PartDefinition {
  const { line, depth, isHalfRack, isRML, fxtMaterials } = context
  
  return {
    mesh: getRackModel(
      `${line}_LRLP`,
      isHalfRack
        ? `${line} top beam ${depth}`
        : `${line} top beam 2`
    ),
    slots: {
      front : createLatPulldownPulley(context),
      back  : createTopBackPulley(context),
    },
    anchor    : 'back',
    materials : fxtMaterials,
    offset    : isRML ? { z: -24 } : { z: -22 },
  }
}

// MARK: Lat Pulldown
export function createLatPulldownPulley(context: LRLPContext): PartDefinition {
  const { line, height, materials } = context
  
  const latPulldownBolts: PartDefinition = {
    mesh  : getRackModel(`${line}_LRLP`, `${line} lat pulldown bolts`),
    slots : {},
    materials,
  }

  const latPulldownPuck = createLatPulldownPuck(context)
  
  const latPulldownRetainer: PartDefinition = {
    mesh  : getRackModel('SHARED_LRLP', 'lat pulldown retainer'),
    slots : {},
  }

  const pulley: PartDefinition = {
    mesh   : getRackModel('SHARED_LRLP', 'lat pulldown'),
    slots  : { bolts: latPulldownBolts },
    materials,
    offset : { x: -0.25 },
  }

  if (height === '100') {
    pulley.anchor = 'top'
    pulley.slots.high = latPulldownRetainer
    pulley.slots.low = latPulldownPuck
  } else {
    pulley.slots.high = latPulldownPuck
  }

  return pulley
}

export function createLatPulldownPuck(context: LRLPContext): PartDefinition {
  const { config, height } = context
  
  const puck: PartDefinition = {
    mesh   : getRackModel('SHARED_LRLP', 'lat pulldown puck'),
    slots  : {},
    anchor : height === '90' ? 'top' : '',
  }

  if (config.latAndLowRowOptions?.latBarSelection) {
    puck.slots.attachment_clip = createLatBarClip(context)
  }

  return puck
}

// MARK: Top Back Pulley
export function createTopBackPulley(context: LRLPContext): PartDefinition {
  const { height, depth, isHalfRack, isRML, isSlimRack, materials } = context
  
  const weightStackPoles: PartDefinition = {
    mesh  : getRackModel('SHARED_LRLP', `stack poles ${height}h`),
    slots : {},
  }

  const topFrontPulleyCable: PartDefinition = {
    mesh  : getRackModel('SHARED_LRLP', isSlimRack ? 'top cable wm' : 'front cable'),
    slots : {},
    materials,
  }

  const pulley: PartDefinition = {
    mesh  : getRackModel('SHARED_LRLP', 'top back pulley'),
    slots : {
      poles     : weightStackPoles,
      top_cable : topFrontPulleyCable,
    },
    materials,
    offset: isRML ? { x: -0.05 } : {},
  }

  if (depth === '4' && isHalfRack) {
    pulley.slots.cable_extension = {
      mesh  : getRackModel('SHARED_LRLP', 'front cable 43'),
      slots : {},
    }
  }

  return pulley
}

// MARK: Back Crossmembers
export function createBackTopCrossmember(context: LRLPContext, topBeam: PartDefinition): PartDefinition {
  const { line, isRM, isRML, isHalfRack, fxtMaterials } = context
  
  const crossmember: PartDefinition = {
    mesh        : getRackModel(`${line}_LRLP`, `${line} crossmember top 4`),
    anchor      : 'snowman_bottom',
    slots       : { ftx: topBeam },
    adjustments : [{ type: 'rotate', axis: 'Y', amount: 90 }],
    offset      : isRM && !isHalfRack ? { y: -1 } : {},
    materials   : fxtMaterials,
  }

  if (!isHalfRack && isRML) {
    crossmember.offset = { y: 1 }
  }

  return crossmember
}

export function createBackBottomCrossmember(context: LRLPContext): PartDefinition {
  const { line, isRML, fxtMaterials } = context
  
  return {
    mesh        : getRackModel(`${line}_LRLP`, `${line} crossmember bottom 4`),
    anchor      : 'back',
    adjustments : [{ type: 'rotate', axis: 'Y', amount: 90 }],
    slots       : {
      middle : createFootplate(context),
      back   : createBackBottomPulley(context),
    },
    materials : fxtMaterials,
    offset    : isRML ? { y: -1 } : {},
  }
}

// MARK: Footplate
export function createFootplate(context: LRLPContext): PartDefinition {
  const { config, materials } = context
  
  const footplate: PartDefinition = {
    mesh  : getRackModel('SHARED_LRLP', 'footplate'),
    slots : {},
    materials,
  }

  if (config.latAndLowRowOptions?.lowRowSelection) {
    footplate.slots.low = createLowRowClip(context)
  }

  return footplate
}

// MARK: Back Bottom Pulley & Weight Stack
export function createBackBottomPulley(context: LRLPContext): PartDefinition {
  const { config, weightType, materials } = context
  
  const weightStackTop: PartDefinition = {
    mesh  : getRackModel('SHARED_LRLP', 'weight stack top'),
    materials,
    slots : {},
  }

  const weights300: PartDefinition = {
    mesh  : getRackModel('SHARED_LRLP', 'weight stack plates 300'),
    slots : {},
  }

  const pulley: PartDefinition = {
    mesh  : getRackModel('SHARED_LRLP', 'bottom back pulley'),
    materials,
    slots : weightType === '250'
      ? { top_250: weightStackTop }
      : { top_300: weightStackTop, weights_300: weights300 },
  }

  if (config.ftShrouds) {
    addShrouds(pulley, context)
  }

  return pulley
}

function addShrouds(pulley: PartDefinition, context: LRLPContext): void {
  const { height, materials } = context
  
  pulley.slots.weight_shroud = {
    mesh   : getRackModel('SHARED_LRLP', 'weight stack shroud'),
    materials,
    slots  : {},
    anchor : 'left',
  }

  pulley.slots.shroud = {
    mesh   : getRackModel('SHARED_LRLP', `shroud ${height}h`),
    materials,
    slots  : {},
    anchor : 'left',
  }
}

// MARK: Stringer Brackets
export function createStringerBracket(offset: object, left: boolean): PartDefinition {
  return {
    mesh        : getRackModel('SHARED_LRLP', 'stringer bracket'),
    slots       : {},
    anchor      : 'bottom',
    adjustments : left ? [] : [{ type: 'rotate', axis: 'Z', amount: 180 }],
    offset,
  }
}

export function createStringer(): PartDefinition {
  return {
    mesh   : getRackModel('SHARED_LRLP', 'stringer'),
    slots  : {},
    anchor : 'left',
    offset : { z: -0.25 },
  }
}

// MARK: Stringer Bolts
export function createRMLBolt(offsetOverride?: object): PartDefinition {
  return {
    mesh   : getRackModel('RML_LRLP', 'rml stringer bolt'),
    slots  : {},
    offset : offsetOverride,
  }
}

export function createRMBolt(offset: object): PartDefinition {
  return {
    mesh        : getRackModel('RM_LRLP', 'rm bottom stringer bolt'),
    slots       : {},
    offset,
    adjustments : [{ type: 'rotate', axis: 'Y', amount: 180 }],
  }
}

// MARK: Attachments
export function createLowRowClip(context: LRLPContext): PartDefinition {
  const { config } = context
  
  const lowRowAttachment: PartDefinition = {
    mesh   : `${ATTACHMENTS}#${config.latAndLowRowOptions?.lowRowSelection}`,
    slots  : {},
    offset : { z: 0.45 },
  }

  return {
    mesh  : `${ATTACHMENTS}#bottom clip`,
    slots : { front: [lowRowAttachment] },
  }
}

export function createLatBarClip(context: LRLPContext): PartDefinition {
  const { config } = context

  const latBarAttachment: PartDefinition = {
    mesh      : `${ATTACHMENTS}#${config.latAndLowRowOptions?.latBarSelection}`,
    slots     : {},
    materials : config.materials?.latpulldown,
  }

  return {
    mesh  : `${ATTACHMENTS}#clip`,
    slots : { bottom: [latBarAttachment] },
  }
}

// MARK: Slim Rack Crossmembers
export function createSlimRackCrossmember(context: LRLPContext, position: 'top' | 'bottom'): PartDefinition {
  const { line, isRM, fxtMaterials } = context
  
  return {
    mesh: getRackModel(`${line}_LRLP`, isRM 
      ? `rm crossmember ${position} 1` 
      : 'rml crossmember 1'
    ),
    slots     : {},
    materials : fxtMaterials,
  }
}